import { Box, Container, Typography } from "@mui/material";

const CustomStepper = ({
  steps = [],
  activeStep = 0,
  containerWidth,
  maxWidth = "100%",
  pillHeight = 12,
  activeColor = "#1976d2",
  completedColor = "#40a9ff",
  pendingColor = "#d1d1d1",
  showNextStep = true,
  showStepCount = true,
  title = "Now",
  inProgressText = "(In Progress)",
  completedText = "(Completed)",
  nextText = "Next",
  completedNextText = "Completed",
  stepText = "Step",
  onStepClick = null, // New prop for step click handler
  allowClickNavigation = true, // New prop to enable/disable click navigation
  clickableSteps = "all", // "all", "completed", "completedAndNext", or array of indices
  sx = {},
}) => {
  // Calculate progress percentage
  const progress =
    steps.length > 0 ? ((activeStep + 1) / steps.length) * 100 : 0;
  const isCompleted = activeStep >= steps.length - 1;

  // Determine if a step is clickable
  const isStepClickable = (stepIndex) => {
    if (!allowClickNavigation || !onStepClick) return false;

    if (Array.isArray(clickableSteps)) {
      return clickableSteps.includes(stepIndex);
    }

    switch (clickableSteps) {
      case "all":
        return true;
      case "completed":
        return stepIndex < activeStep;
      case "completedAndNext":
        return stepIndex <= activeStep + 1;
      case "completedAndCurrent":
        return stepIndex <= activeStep;
      default:
        return false;
    }
  };

  // Handle step click
  const handleStepClick = (stepIndex) => {
    if (isStepClickable(stepIndex) && onStepClick) {
      onStepClick(stepIndex, steps[stepIndex]);
    }
  };

  // Get step cursor style
  const getStepCursor = (stepIndex) => {
    return isStepClickable(stepIndex) ? "pointer" : "default";
  };

  // Get step hover effects
  const getStepHoverEffects = (stepIndex) => {
    if (!isStepClickable(stepIndex)) return {};

    const baseColor =
      stepIndex === activeStep
        ? activeColor
        : stepIndex < activeStep
        ? completedColor
        : pendingColor;

    return {
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: `0 4px 8px rgba(0,0,0,0.15), 0 0 0 2px ${baseColor}60`,
        filter: "brightness(1.1)",
      },
    };
  };

  return (
    <Container
      sx={{
        width: "100%",
      }}
    >
      <Box
        position="sticky"
        top={0}
        zIndex={10}
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
          borderRadius: { xs: 1, sm: 2 },
          bgcolor: "white",
          boxShadow: {
            xs: "0 1px 2px rgba(0,0,0,0.08)",
            sm: "0 1px 3px rgba(0,0,0,0.1)",
            md: "0 2px 4px rgba(0,0,0,0.1)",
          },
          margin: "0 auto",
          ...sx,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", md: "space-between" }}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          gap={{ xs: 2, md: 1 }}
        >
          {/* Left side: Current Step + Stepper */}
          <Box
            flex={{ xs: "none", md: 1 }}
            minWidth={{ xs: "100%", md: "250px" }}
          >
            <Typography
              variant="body1"
              mb={1}
              sx={{
                fontSize: { xs: 13, sm: 14, md: 14.5, lg: 15 },
                fontFamily: "Manrope",
                lineHeight: { xs: 1.3, sm: 1.4 },
                wordBreak: "break-word",
              }}
            >
              <Box component="span" fontWeight="bold">
                {title}:
              </Box>{" "}
              <Box
                component="span"
                sx={{
                  display: { xs: "block", sm: "inline" },
                }}
              >
                {steps[activeStep] || "Unknown Step"}
              </Box>{" "}
              <Box
                component="span"
                fontWeight="bold"
                sx={{
                  display: { xs: "block", sm: "inline" },
                }}
              >
                {isCompleted ? completedText : inProgressText}
              </Box>
            </Typography>

            {/* Pill Progress Bar */}
            <Box
              display="flex"
              gap={{ xs: 1, sm: 1.5, md: 2 }}
              mt={2}
              sx={{
                width: "100%",
                overflowX: { xs: "auto", sm: "visible" },
                pb: { xs: 0.5, sm: 0 },
                // Hide scrollbar on webkit browsers
                "&::-webkit-scrollbar": {
                  height: 2,
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#ccc",
                  borderRadius: 1,
                },
              }}
            >
              {steps.map((step, index) => (
                <Box
                  key={index}
                  onClick={() => handleStepClick(index)}
                  title={isStepClickable(index) ? `Navigate to: ${step}` : step}
                  sx={{
                    // Responsive flex behavior
                    flex: { xs: "0 0 auto", sm: 1 },
                    // Responsive width
                    width: {
                      xs: Math.max(
                        60,
                        Math.min(120, 350 / Math.max(steps.length, 1) - 8)
                      ),
                      sm: "auto",
                    },
                    minWidth: {
                      xs: 60,
                      sm: 80,
                      md: 100,
                      lg: 120,
                    },
                    maxWidth: {
                      xs: 120,
                      sm: 150,
                      md: 180,
                      lg: 200,
                    },
                    height: {
                      xs: Math.max(pillHeight - 2, 8),
                      sm: Math.max(pillHeight - 1, 10),
                      md: pillHeight,
                      lg: pillHeight + 1,
                    },
                    borderRadius: { xs: 6, sm: 8 },
                    bgcolor:
                      index === activeStep
                        ? activeColor
                        : index < activeStep
                        ? completedColor
                        : pendingColor,
                    cursor: getStepCursor(index),
                    transition: "all 0.3s ease",
                    boxShadow:
                      index === activeStep
                        ? {
                            xs: `0 0 0 1px ${activeColor}30`,
                            sm: `0 0 0 1px ${activeColor}40`,
                          }
                        : "none",
                    // Add hover effects for clickable steps
                    ...getStepHoverEffects(index),
                    // Add active state for better UX
                    "&:active": isStepClickable(index)
                      ? {
                          transform: "translateY(0px)",
                          transition: "all 0.1s ease",
                        }
                      : {},
                    // Add focus styles for accessibility
                    "&:focus": isStepClickable(index)
                      ? {
                          outline: `2px solid ${activeColor}`,
                          outlineOffset: "2px",
                        }
                      : {},
                    // Make it focusable if clickable
                    ...(isStepClickable(index) ? { tabIndex: 0 } : {}),
                  }}
                  // Add keyboard navigation
                  onKeyDown={(e) => {
                    if (
                      isStepClickable(index) &&
                      (e.key === "Enter" || e.key === " ")
                    ) {
                      e.preventDefault();
                      handleStepClick(index);
                    }
                  }}
                />
              ))}
            </Box>

            {/* Click instruction text for mobile */}
            {allowClickNavigation && onStepClick && (
              <Typography
                variant="caption"
                sx={{
                  display: { xs: "block", md: "none" },
                  mt: 1,
                  fontSize: 11,
                  color: "text.secondary",
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                Tap any step to navigate
              </Typography>
            )}
          </Box>

          {/* Right side: Next + Step Count */}
          {showNextStep && (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "row", md: "column" },
                justifyContent: { xs: "space-between", md: "flex-start" },
                alignItems: { xs: "center", md: "flex-end" },
                minWidth: { xs: "100%", md: "120px", lg: "150px" },
                pt: { xs: 1.5, md: 0 },
                borderTop: { xs: "1px solid #f0f0f0", md: "none" },
                gap: { xs: 0, md: 1 },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: 12, sm: 13, md: 14, lg: 14.5 },
                  fontFamily: "Manrope",
                  lineHeight: { xs: 1.2, sm: 1.3 },
                  textAlign: { xs: "left", md: "right" },
                  mb: { xs: 0, md: 1 },
                  flex: { xs: 1, md: "none" },
                  mr: { xs: 2, md: 0 },
                }}
              >
                <Box component="span" fontWeight="bold">
                  {nextText}:
                </Box>{" "}
                <Box
                  component="span"
                  sx={{
                    display: { xs: "block", sm: "inline" },
                    mt: { xs: 0.2, sm: 0 },
                  }}
                >
                  {activeStep < steps.length - 1
                    ? steps[activeStep + 1]
                    : completedNextText}
                </Box>
              </Typography>

              {showStepCount && (
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14, lg: 15 },
                    fontFamily: "Manrope",
                    fontWeight: 600,
                    textAlign: "right",
                    mt: { xs: 0, md: 0.5 },
                    whiteSpace: "nowrap",
                  }}
                >
                  {stepText} {activeStep + 1}/{steps.length}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Mobile Progress Bar */}
        <Box
          sx={{
            mt: 2,
            display: { xs: "flex", sm: "none" },
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: 1,
              height: 2,
              bgcolor: pendingColor,
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                bgcolor: activeColor,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              color: "text.secondary",
              fontWeight: 500,
              minWidth: "fit-content",
            }}
          >
            {Math.round(progress)}%
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default CustomStepper;
